import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Handshake, Timer, AlertTriangle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsolePartners, formatFcfa, type ConsolePartnerRow } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA partner / wholesaler fulfillment board (Epic 8) — the ops view over
// partner-routed orders and their SLA. Each row is a fulfillment handed to a
// hub: routed → accepted → dispatched → completed (or rerouted on breach).
// Reads are RLS-scoped (migrations 131-140); a non-authorized JWT sees nothing.
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  routed: "Routé",
  accepted: "Accepté",
  dispatched: "Expédié",
  completed: "Terminé",
  rerouted: "Réacheminé",
};

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function PartnersBoard() {
  const q = useQuery({ queryKey: ["djayma", "console-partners"], queryFn: fetchConsolePartners, retry: false });

  const rows: ConsolePartnerRow[] = q.data ?? [];
  const breachedCount = rows.filter((r) => r.sla_breached).length;
  const completedCount = rows.filter((r) => r.status === "completed").length;
  const totalXof = rows.reduce((acc, r) => acc + Number(r.total_xof ?? 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Handshake className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Fulfillment partenaires</h1>
            <p className="text-sm text-stone-500">
              Commandes routées vers les hubs partenaires — suivi des délais et des SLA
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Fulfillment partenaires non joignable — vérifiez les migrations 131-140 et l&apos;exposition du schéma{" "}
            <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            icon={<Handshake className="h-5 w-5" />}
            tone="bg-stone-100 text-stone-700"
            label="Fulfillments totaux"
            value={rows.length}
          />
          <Kpi
            icon={<AlertTriangle className="h-5 w-5" />}
            tone="bg-rose-100 text-rose-700"
            label="SLA dépassés"
            value={breachedCount}
          />
          <Kpi
            icon={<Timer className="h-5 w-5" />}
            tone="bg-emerald-100 text-emerald-700"
            label="Terminés"
            value={completedCount}
          />
          <Kpi
            icon={<Handshake className="h-5 w-5" />}
            tone="bg-amber-100 text-amber-700"
            label="Montant total"
            value={formatFcfa(totalXof)}
          />
        </div>

        {/* Fulfillments table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Fulfillments partenaires</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucun fulfillment partenaire.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Hub</th>
                    <th className="py-2 pr-4 font-medium">Boutique</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                    <th className="py-2 pr-4 font-medium text-right">Montant</th>
                    <th className="py-2 pr-4 font-medium">Échéance SLA</th>
                    <th className="py-2 pr-4 font-medium">SLA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-4 text-stone-700">{r.hub}</td>
                      <td className="py-2 pr-4">
                        <div className="text-stone-700">{r.shop_name}</div>
                        <div className="text-xs text-stone-400">{r.zone}</div>
                      </td>
                      <td className="py-2 pr-4 text-stone-500">{STATUS_LABELS[r.status] ?? r.status}</td>
                      <td className="py-2 pr-4 text-right font-medium text-stone-800">{formatFcfa(r.total_xof)}</td>
                      <td className="py-2 pr-4 text-stone-500">{new Date(r.sla_deadline).toLocaleString("fr-FR")}</td>
                      <td className="py-2 pr-4">
                        {r.sla_breached ? (
                          <span className="inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                            SLA dépassé
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            Dans les délais
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
